import React, { useState } from 'react';
import { Modal, Box, Button } from '@mui/material';

import { apiRequest } from '@/src/services';
import { Loading, ErrorMessage, StatusMessage } from '@/src/ui';

interface SyncAttemptsModalProps {
    open: boolean;
    assignmentId: number | null;
    handleClose: (_shouldRefresh: boolean, _isInTimeout: boolean) => void;
}

const SyncAttemptsModal: React.FC<SyncAttemptsModalProps> = ({
    open,
    assignmentId,
    handleClose,
}) => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [firstConfirm, setFirstConfirm] = useState(false);

    const handleSubmit = async (e: React.FormEvent<any>) => {
        e.preventDefault();

        if (submitting || success) return;

        if (!firstConfirm) {
            setFirstConfirm(true);
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            await apiRequest<number>('POST', `assessments/examAssignments/sync/${assignmentId}`);
            setSuccess(true);
            setTimeout(() => {
                handleClose(true, false);
                setFirstConfirm(false);
                setSuccess(false);
            }, 750);
        } catch {
            setError('Failed to sync attempts');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={() => handleClose(false, success)}>
            <Box sx={{ p: 4, bgcolor: 'background.paper', margin: 'auto', mt: 8, width: 400 }}>
                <h2>Sync Attempts</h2>
                <br />
                <div>
                    Sync all linked attempts with the latest question versions and re-grade
                    completed attempts? This cannot be undone.
                </div>
                <br />
                <div>Assignment ID: {assignmentId}</div>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    {!firstConfirm ? (
                        <>
                            <Button variant="outlined" onClick={() => handleClose(false, false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="error" onClick={handleSubmit}>
                                Sync
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                disabled={submitting || success}
                                variant="contained"
                                color="error"
                                onClick={handleSubmit}
                            >
                                Confirm Sync
                            </Button>
                            <Button
                                disabled={submitting || success}
                                variant="outlined"
                                onClick={() => handleClose(false, false)}
                            >
                                Cancel
                            </Button>
                        </>
                    )}
                </Box>
                <ErrorMessage error={error} />
                <StatusMessage
                    visible={success}
                    status="Assessment attempts synced successfully!"
                    style={{ marginTop: 8 }}
                />
                <Loading visible={submitting} message="Processing..." size="sm" />
            </Box>
        </Modal>
    );
};

export default SyncAttemptsModal;
